import * as Dialog from "@radix-ui/react-dialog";
import { KLAVIYO_METRICS } from "@logiful/domain";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Blocks, Headset, KeyRound, MessagesSquare, Pause, Play, Send, Trash2, Workflow } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";
import { PageHeader, useTenant } from "@/components/AppShell";
import { Badge, Button, Card, cx, Input, Skeleton } from "@/components/ui";
import { api, ApiError, canManage, type AppsView } from "@/lib/api";
import { fmtRelative } from "@/lib/format";

import { t, msg } from "@/lib/i18n";
const errMsg = (e: unknown, f = "Aktion fehlgeschlagen") => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? f) : f);
const metricOf = (event: string) => (KLAVIYO_METRICS as Record<string, string>)[event] ?? event;

function DialogShell({ open, onClose, title, description, children }: { open: boolean; onClose: () => void; title: string; description?: string; children: ReactNode }) {
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30 animate-fade-in" />
        <Dialog.Content className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[90vh] overflow-y-auto rounded-2xl border border-border bg-surface p-6 shadow-2xl animate-pop-in w-[min(600px,calc(100vw-32px))]">
          <Dialog.Title className="text-[17px] font-semibold">{t(title)}</Dialog.Title>
          {description && <Dialog.Description className="text-fg-muted mt-1 text-[13px]">{t(description)}</Dialog.Description>}
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Ereignisgruppen wählen – die Gruppe ist an, wenn alle ihre Ereignisse abonniert sind */
function GroupPicker({ groups, events, onChange, disabled }: { groups: AppsView["eventGroups"]; events: string[]; onChange: (e: string[]) => void; disabled?: boolean }) {
  return (
    <div className="space-y-2">
      {groups.map((g) => {
        const on = g.events.every((e) => events.includes(e));
        return (
          <label key={g.key} className={cx("flex gap-2.5 rounded-xl border p-3", disabled ? "opacity-70" : "cursor-pointer", on ? "border-ink bg-surface-2/60" : "border-border hover:bg-surface-2/40")}>
            <input type="checkbox" className="mt-0.5 accent-ink" disabled={disabled} checked={on} onChange={(ev) => onChange(ev.target.checked ? [...new Set([...events, ...g.events])] : events.filter((e) => !g.events.includes(e)))} />
            <span className="min-w-0">
              <span className="block text-[13px] font-medium">{t(g.label)}</span>
              <span className="block text-[12px] text-fg-muted">{t(g.hint)}</span>
              <span className="block mt-1 text-[11.5px] text-fg-subtle font-mono">{g.events.map(metricOf).join(" · ")}</span>
            </span>
          </label>
        );
      })}
    </div>
  );
}

function KeyField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <label className="block">
      <span className="text-[12px] font-medium text-fg-muted">{t("Privater API-Schlüssel")}</span>
      <Input autoFocus type="password" autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} placeholder={t("pk_…")} className="mt-1 font-mono" />
      <span className="block mt-1.5 text-[12px] text-fg-subtle">
        {t("In Klaviyo:")} <b className="text-fg-muted">{t("Einstellungen → Konto → API-Schlüssel → Privaten API-Schlüssel erstellen")}</b>{t(", Name „Logiful“. Berechtigungen:")} <b className="text-fg-muted">{t("Konten")}</b> {t("lesen,")} <b className="text-fg-muted">{t("Events")}</b> {t("und")} <b className="text-fg-muted">{t("Profile")}</b> {t("vollständig. Der Schlüssel wird verschlüsselt gespeichert und nie wieder angezeigt.")}
      </span>
    </label>
  );
}

function ConnectDialog({ open, onClose, groups }: { open: boolean; onClose: () => void; groups: AppsView["eventGroups"] }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const [key, setKey] = useState("");
  const [events, setEvents] = useState<string[]>(() => groups.flatMap((g) => g.events));
  const connect = useMutation({
    mutationFn: () => api.apps.connectKlaviyo(tenantId, { apiKey: key.trim(), events }),
    onSuccess: (r) => { toast.success(r.account ? `Mit Klaviyo verbunden: ${r.account}` : "Mit Klaviyo verbunden"); void qc.invalidateQueries({ queryKey: ["apps", tenantId] }); setKey(""); onClose(); },
    onError: (e) => toast.error(errMsg(e, "Verbinden fehlgeschlagen")),
  });
  return (
    <DialogShell open={open} onClose={onClose} title={t("Klaviyo verbinden")} description={t("Logiful meldet ausgewählte Ereignisse an das Profil eurer Kundinnen und Kunden – ihr baut daraus Flows im eigenen Design.")}>
      <div className="mt-4 space-y-4">
        <KeyField value={key} onChange={setKey} />
        <div>
          <p className="text-[12px] font-medium text-fg-muted mb-1.5">{t("Welche Ereignisse?")}</p>
          <GroupPicker groups={groups} events={events} onChange={setEvents} />
        </div>
        <p className="rounded-xl border border-border bg-surface-2/60 px-3 py-2 text-[12px] text-fg-muted">
          {t("Übertragen werden E-Mail, Name und Ort der Lieferadresse sowie Auftragsnummer, Artikel und Sendungsnummer. Ihr verarbeitet diese Daten in Klaviyo als Verantwortliche – Werbemails nur mit Einwilligung, Versand- und Zustellinfos sind Transaktionsmails.")}
        </p>
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose}>{t("Abbrechen")}</Button>
        <Button variant="primary" loading={connect.isPending} disabled={!key.trim() || !events.length} onClick={() => connect.mutate()}>{t("Prüfen & verbinden")}</Button>
      </div>
    </DialogShell>
  );
}

function KlaviyoConnected({ view }: { view: AppsView }) {
  const k = view.klaviyo!;
  const { tenantId, role, me } = useTenant();
  const editable = canManage(role);
  const qc = useQueryClient();
  const refresh = () => void qc.invalidateQueries({ queryKey: ["apps", tenantId] });
  const [events, setEvents] = useState(k.events);
  const [testOpen, setTestOpen] = useState(false);
  const [email, setEmail] = useState(me.user.email);
  const [keyOpen, setKeyOpen] = useState(false);
  const [newKey, setNewKey] = useState("");
  const log = useQuery({ queryKey: ["klaviyo-log", tenantId], queryFn: () => api.apps.klaviyoDeliveries(tenantId), refetchInterval: 30_000 });
  const save = useMutation({ mutationFn: () => api.apps.updateKlaviyo(tenantId, { events }), onSuccess: () => { toast.success("Ereignisse gespeichert"); refresh(); }, onError: (e) => toast.error(errMsg(e)) });
  const toggle = useMutation({ mutationFn: () => api.apps.updateKlaviyo(tenantId, { active: !k.active }), onSuccess: () => { toast.success(k.active ? "Pausiert – Ereignisse werden gesammelt und bis zu 24 Stunden nachgeliefert" : "Wieder aktiv"); refresh(); }, onError: (e) => toast.error(errMsg(e)) });
  const replaceKey = useMutation({ mutationFn: () => api.apps.updateKlaviyo(tenantId, { apiKey: newKey.trim() }), onSuccess: () => { toast.success("Neuer Schlüssel gespeichert"); setKeyOpen(false); setNewKey(""); refresh(); }, onError: (e) => toast.error(errMsg(e)) });
  const disconnect = useMutation({ mutationFn: () => api.apps.disconnectKlaviyo(tenantId), onSuccess: () => { toast.success("Klaviyo getrennt"); refresh(); } });
  const test = useMutation({ mutationFn: () => api.apps.klaviyoTest(tenantId, email.trim()), onSuccess: (r) => { toast.success(r.message); setTestOpen(false); void log.refetch(); }, onError: (e) => toast.error(errMsg(e, "Test fehlgeschlagen")) });
  const dirty = events.length !== k.events.length || events.some((e) => !k.events.includes(e));

  return (
    <>
      <div className="px-5 pb-4 flex flex-wrap items-center gap-2 text-[12.5px]">
        {k.active ? (k.consecutiveFailures ? <Badge tone="warning">{k.consecutiveFailures} {t("Fehler in Folge")}</Badge> : <Badge tone="success">{t("verbunden")}</Badge>) : <Badge tone="danger">{t("pausiert")}</Badge>}
        <span className="text-fg-muted">{t("Konto")} <b className="text-fg">{k.account ?? t("Klaviyo")}</b> {t("· seit")} {fmtRelative(k.createdAt)}{k.lastSuccessAt ? t(" · zuletzt übertragen {v}", { v: fmtRelative(k.lastSuccessAt) }) : t(" · noch nichts übertragen")}</span>
        {editable && (
          <div className="ml-auto flex flex-wrap gap-1">
            <Button size="sm" variant="ghost" icon={<Send className="size-3.5" />} onClick={() => setTestOpen(true)}>{t("Testereignis")}</Button>
            <Button size="sm" variant="ghost" icon={k.active ? <Pause className="size-3.5" /> : <Play className="size-3.5" />} loading={toggle.isPending} onClick={() => toggle.mutate()}>{k.active ? t("Pausieren") : t("Aktivieren")}</Button>
            <Button size="sm" variant="ghost" icon={<KeyRound className="size-3.5" />} onClick={() => setKeyOpen(true)}>{t("Schlüssel ersetzen")}</Button>
            <Button size="sm" variant="ghost" icon={<Trash2 className="size-3.5" />} onClick={() => confirm("Klaviyo trennen? Es werden keine Ereignisse mehr übertragen; eure Flows in Klaviyo bleiben bestehen.") && disconnect.mutate()}>{t("Trennen")}</Button>
          </div>
        )}
      </div>
      {k.disabledReason && <p className="mx-5 mb-4 rounded-xl border border-danger/25 bg-danger-soft/50 px-3 py-2 text-[12.5px] text-danger">{k.disabledReason}</p>}

      <div className="grid gap-5 border-t border-border p-5 lg:grid-cols-2">
        <section>
          <h3 className="text-[13px] font-semibold mb-2">{t("Ereignisse")}</h3>
          <GroupPicker groups={view.eventGroups} events={events} onChange={setEvents} disabled={!editable} />
          {dirty && (
            <div className="mt-3 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setEvents(k.events)}>{t("Verwerfen")}</Button>
              <Button size="sm" variant="primary" disabled={!events.length} loading={save.isPending} onClick={() => save.mutate()}>{t("Speichern")}</Button>
            </div>
          )}
        </section>
        <section>
          <h3 className="text-[13px] font-semibold mb-2">{t("So nutzt ihr es in Klaviyo")}</h3>
          <ol className="list-decimal pl-4 space-y-1.5 text-[12.5px] text-fg-muted">
            <li>{t("Testereignis an die eigene Adresse senden – danach kennt Klaviyo die Metriken „Logiful: …“.")}</li>
            <li>{t("In Klaviyo")} <b className="text-fg">{t("Flows → Flow erstellen")}</b>{t(", Auslöser: Metrik, z. B.")} <span className="font-mono">{t("Logiful: Versendet")}</span>.</li>
            <li>{t("In der Mail die Felder des Ereignisses nutzen:")}</li>
          </ol>
          <div className="mt-2 rounded-xl border border-border bg-surface-2/60 p-3 font-mono text-[11.5px] leading-relaxed text-fg-muted">
            {t("{{ event.OrderNumber }}")} · {t("{{ event.TrackingNumber }}")} · {t("{{ event.TrackingUrl }}")} · {t("{{ event.Carrier }}")} · {t("{{ event.StatusText }}")} · {t("{% for item in event.Items %}{{ item.Name }}{% endfor %}")}
          </div>
          <p className="mt-2 text-[12px] text-fg-subtle">{t("Mehrere Pakete: jedes Paket meldet seinen Zustellstatus einzeln. Ohne E-Mail in der Lieferadresse (z. B. Marktplätze) wird nichts übertragen.")}</p>
        </section>
      </div>

      <div className="border-t border-border">
        <div className="px-5 pt-4 pb-2 flex items-baseline gap-2"><h3 className="text-[13px] font-semibold">{t("Übertragungen")}</h3><span className="text-[12px] text-fg-subtle">{t("letzte 50 · fehlgeschlagene werden bis zu 24 Stunden wiederholt")}</span></div>
        {log.isLoading && <div className="px-5 pb-4"><Skeleton className="h-4 w-1/2" /></div>}
        {log.data?.length === 0 && <p className="px-5 pb-5 text-[12.5px] text-fg-muted">{t("Noch nichts übertragen. Das erste Ereignis kommt mit dem nächsten Versand.")}</p>}
        <ul className="divide-y divide-border text-[12.5px]">
          {log.data?.map((d) => (
            <li key={d.id} className="px-5 py-2 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="font-medium">{metricOf(d.event)}</span>
              {d.orderNumber && <span className="font-mono text-fg-muted">{d.orderNumber}</span>}
              <span className="text-fg-subtle">{fmtRelative(d.createdAt)}</span>
              <span className="ml-auto">
                {d.note ? <Badge tone="neutral">{d.note.replace(/^übersprungen: /, "übersprungen – ")}</Badge> : d.status === "success" ? <Badge tone="success">{t("übertragen")}</Badge> : d.status === "pending" ? <Badge tone="warning">{d.attempts ? t("wird wiederholt · {v}", { v: d.error ?? "" }) : t("ausstehend")}</Badge> : <Badge tone="danger">{t("aufgegeben ·")} {d.error}</Badge>}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <DialogShell open={testOpen} onClose={() => setTestOpen(false)} title={t("Testereignis senden")} description={t("Sendet „Logiful: Versendet“ mit einem Beispielauftrag an diese Adresse – ideal, um einen Flow auszuprobieren.")}>
        <label className="block mt-4">
          <span className="text-[12px] font-medium text-fg-muted">{t("E-Mail-Adresse (eigene)")}</span>
          <Input autoFocus type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1" />
        </label>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setTestOpen(false)}>{t("Abbrechen")}</Button>
          <Button variant="primary" loading={test.isPending} disabled={!email.trim()} onClick={() => test.mutate()}>{t("Senden")}</Button>
        </div>
      </DialogShell>
      <DialogShell open={keyOpen} onClose={() => setKeyOpen(false)} title={t("API-Schlüssel ersetzen")} description={t("Der neue Schlüssel wird zuerst bei Klaviyo geprüft – erst dann ersetzt er den bisherigen.")}>
        <div className="mt-4"><KeyField value={newKey} onChange={setNewKey} /></div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setKeyOpen(false)}>{t("Abbrechen")}</Button>
          <Button variant="primary" loading={replaceKey.isPending} disabled={!newKey.trim()} onClick={() => replaceKey.mutate()}>{t("Prüfen & speichern")}</Button>
        </div>
      </DialogShell>
    </>
  );
}

const COMING: { icon: typeof Headset; title: string; text: string }[] = [
  { icon: Headset, title: msg("Kundenservice"), text: msg("Gorgias, Zendesk, Freshdesk: Auftragsstatus, Sendung und Retoure direkt im Ticket.") },
  { icon: MessagesSquare, title: msg("Team-Benachrichtigungen"), text: msg("Slack und Microsoft Teams: Handlungsbedarf, Zustellprobleme und Fehlbestand im Team-Kanal.") },
  { icon: Workflow, title: msg("Automatisierung"), text: msg("Zapier und Make: alle Logiful-Ereignisse als Auslöser für eigene Abläufe.") },
];

/** Einstellungen → Tool-Anbindungen: Klaviyo, weitere Tools folgen */
export function AppsPage() {
  const { tenantId, role } = useTenant();
  const q = useQuery({ queryKey: ["apps", tenantId], queryFn: () => api.apps.get(tenantId) });
  const [connectOpen, setConnectOpen] = useState(false);
  return (
    <div className="pb-16">
      <PageHeader title={t("Tool-Anbindungen")} description={t("Logiful-Ereignisse direkt in euren E-Commerce-Tools nutzen – ohne eigene Entwicklung.")} />
      <div className="px-4 md:px-8 space-y-6 max-w-5xl">
        <Card className="overflow-hidden">
          <div className="p-5 flex flex-wrap items-start gap-3">
            <span className="size-9 rounded-xl grid place-items-center bg-surface-2 text-fg-muted"><Blocks className="size-4" /></span>
            <div className="min-w-0 flex-1">
              <h2 className="font-semibold">{t("Klaviyo")}</h2>
              <p className="text-[12.5px] text-fg-muted mt-0.5">{t("Versendet, In Zustellung, Zugestellt, Retoure geprüft und Lieferverzögerung landen als Ereignis am Profil eurer Kundinnen und Kunden. Daraus baut ihr Versand- und Zustellmails im eigenen Design – mit echten Lagerdaten statt Shop-Schätzungen.")}</p>
            </div>
            {q.data && !q.data.klaviyo && canManage(role) && <Button variant="primary" onClick={() => setConnectOpen(true)}>{t("Verbinden")}</Button>}
          </div>
          {q.isLoading && <div className="px-5 pb-5"><Skeleton className="h-16 w-full" /></div>}
          {q.data?.klaviyo && <KlaviyoConnected key={q.data.klaviyo.id + q.data.klaviyo.events.join()} view={q.data} />}
          {q.data && !q.data.klaviyo && (
            <div className="border-t border-border px-5 py-4 text-[12.5px] text-fg-muted">
              <b className="text-fg">{t("Wieder vorrätig:")}</b> {t("Für Shopify-Shops funktioniert Klaviyos eigene „Back in Stock“-Funktion bereits – Logiful meldet den Bestand an Shopify, Klaviyo liest ihn von dort.")}
            </div>
          )}
          {q.data && <ConnectDialog open={connectOpen} onClose={() => setConnectOpen(false)} groups={q.data.eventGroups} />}
        </Card>

        <section>
          <h2 className="px-1 pb-2 text-[11px] font-semibold uppercase tracking-wide text-fg-subtle">{t("In Vorbereitung")}</h2>
          <div className="grid gap-3 md:grid-cols-3">
            {COMING.map((c) => (
              <Card key={c.title} className="p-4">
                <div className="flex items-center gap-2"><c.icon className="size-4 text-fg-muted" /><span className="text-[13.5px] font-medium">{t(c.title)}</span><Badge tone="neutral" className="ml-auto">{t("bald")}</Badge></div>
                <p className="mt-1.5 text-[12.5px] text-fg-muted">{t(c.text)}</p>
              </Card>
            ))}
          </div>
          <p className="mt-2 px-1 text-[12px] text-fg-subtle">{t("Ihr nutzt ein anderes Tool? Sagt uns Bescheid – bis dahin geht fast alles über Webhooks unter Einstellungen → Schnittstelle.")}</p>
        </section>
      </div>
    </div>
  );
}
